/**
 * Cache Service - SaMD Compliant
 * Provides intelligent caching for API responses and S3 URLs
 * 
 * SaMD Compliance Features:
 * - In-memory only (no PHI persistence on device)
 * - Time-based expiration (TTL) for data freshness
 * - Audit logging for regulatory compliance
 * - Automatic cleanup of expired entries
 * - Cache statistics for monitoring
 * - Session-based lifecycle (cleared on logout/refresh)
 * 
 * Regulatory Compliance:
 * - HIPAA: No PHI stored in persistent storage
 * - FDA 21 CFR Part 11: Audit trail for cache operations
 * - Data integrity: Automatic expiration ensures fresh clinical data
 */

class CacheService {
  constructor() {
    this.cache = new Map();
    this.stats = {
      hits: 0,
      misses: 0,
      sets: 0,
      evictions: 0
    };
    
    // Audit log for regulatory compliance
    this.auditLog = [];
    this.maxAuditLogSize = 1000; // Keep last 1000 operations
    
    // SaMD safety limits
    this.maxCacheSize = 500; // Prevent memory bloat
    this.maxCacheSizeMB = 50; // Maximum 50MB cache
    
    // Default TTL values (in milliseconds)
    // Conservative TTLs for SaMD to ensure data freshness
    this.defaultTTL = {
      falls: 2 * 60 * 1000,        // 2 minutes - critical clinical data
      analytics: 5 * 60 * 1000,    // 5 minutes - analytics can be cached longer
      residents: 5 * 60 * 1000,    // 5 minutes - reduced from 10 for SaMD safety
      photos: 30 * 60 * 1000,      // 30 minutes - S3 presigned URLs valid for 1 hour
      videos: 3 * 60 * 1000,       // 3 minutes - video URLs expire quickly
    };
    
    // Start periodic cleanup
    this.startCleanup();
    
    // Clear cache on page unload (SaMD best practice)
    this.setupSessionCleanup();
    
    // Log initialization
    this.logAudit('CACHE_INIT', { timestamp: new Date().toISOString() });
  }

  /**
   * Generate cache key from parameters
   */
  generateKey(prefix, ...params) {
    return `${prefix}:${params.join(':')}`;
  }

  /**
   * Set cache entry with TTL
   * SaMD Enhancement: Size limits and audit logging
   */
  set(key, value, ttl = null) {
    // Check cache size limits (SaMD safety)
    if (this.cache.size >= this.maxCacheSize) {
      this.evictOldest();
    }
    
    // Check memory limits
    const estimatedSize = this.estimateMemoryUsageBytes();
    if (estimatedSize > this.maxCacheSizeMB * 1024 * 1024) {
      this.evictOldest();
    }
    
    const expiresAt = Date.now() + (ttl || this.defaultTTL.falls);
    
    this.cache.set(key, {
      value,
      expiresAt,
      createdAt: Date.now()
    });
    
    this.stats.sets++;
    
    // Audit log for regulatory compliance
    this.logAudit('CACHE_SET', { key: this.sanitizeKeyForLog(key), ttl });
  }

  /**
   * Get cache entry if not expired
   * SaMD Enhancement: Audit logging for data access
   */
  get(key) {
    const entry = this.cache.get(key);
    
    if (!entry) {
      this.stats.misses++;
      this.logAudit('CACHE_MISS', { key: this.sanitizeKeyForLog(key) });
      return null;
    }
    
    // Check if expired
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      this.stats.misses++;
      this.stats.evictions++;
      this.logAudit('CACHE_EXPIRED', { key: this.sanitizeKeyForLog(key) });
      return null;
    }
    
    this.stats.hits++;
    this.logAudit('CACHE_HIT', { key: this.sanitizeKeyForLog(key) });
    return entry.value;
  }

  /**
   * Check if key exists and is not expired
   */
  has(key) {
    const entry = this.cache.get(key);
    if (!entry) return false;
    
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return false;
    }
    
    return true;
  }

  /**
   * Delete specific cache entry
   */
  delete(key) {
    return this.cache.delete(key);
  }

  /**
   * Clear all cache entries matching a pattern
   */
  clearPattern(pattern) {
    let count = 0;
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
        count++;
      }
    }
    return count;
  }

  /**
   * Clear all cache
   * SaMD Enhancement: Audit logging and secure cleanup
   */
  clearAll() {
    const size = this.cache.size;
    this.cache.clear();
    this.logAudit('CACHE_CLEAR_ALL', { entriesCleared: size });
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const hitRate = this.stats.hits + this.stats.misses > 0
      ? (this.stats.hits / (this.stats.hits + this.stats.misses) * 100).toFixed(2)
      : 0;
    
    return {
      ...this.stats,
      hitRate: `${hitRate}%`,
      size: this.cache.size,
      memoryUsage: this.estimateMemoryUsage()
    };
  }

  /**
   * Estimate memory usage in bytes (for limits checking)
   */
  estimateMemoryUsageBytes() {
    let bytes = 0;
    for (const [key, entry] of this.cache.entries()) {
      bytes += key.length * 2; // UTF-16 characters
      bytes += JSON.stringify(entry.value).length * 2;
      bytes += 16; // timestamps
    }
    return bytes;
  }
  
  /**
   * Estimate memory usage (human readable)
   */
  estimateMemoryUsage() {
    const bytes = this.estimateMemoryUsageBytes();
    
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  /**
   * Periodic cleanup of expired entries
   */
  startCleanup() {
    setInterval(() => {
      const now = Date.now();
      
      for (const [key, entry] of this.cache.entries()) {
        if (now > entry.expiresAt) {
          this.cache.delete(key);
          this.stats.evictions++;
        }
      }
    }, 60 * 1000); // Run every minute
  }

  /**
   * Invalidate cache for a specific resident
   * Useful when resident data is updated
   */
  invalidateResident(residentId) {
    const count = this.clearPattern(residentId);
    this.logAudit('CACHE_INVALIDATE_RESIDENT', { residentId, entriesCleared: count });
  }

  /**
   * Invalidate all falls-related cache
   * Useful when new fall is detected
   */
  invalidateFalls() {
    const fallsCount = this.clearPattern('falls:');
    const analyticsCount = this.clearPattern('analytics:');
    this.logAudit('CACHE_INVALIDATE_FALLS', { 
      fallsEntriesCleared: fallsCount,
      analyticsEntriesCleared: analyticsCount 
    });
  }
  
  /**
   * Evict oldest cache entries (LRU-style)
   * SaMD Safety: Prevent memory bloat
   */
  evictOldest() {
    let oldestKey = null;
    let oldestTime = Infinity;
    
    for (const [key, entry] of this.cache.entries()) {
      if (entry.createdAt < oldestTime) {
        oldestTime = entry.createdAt;
        oldestKey = key;
      }
    }
    
    if (oldestKey) {
      this.cache.delete(oldestKey);
      this.stats.evictions++;
      this.logAudit('CACHE_EVICT_OLDEST', { key: this.sanitizeKeyForLog(oldestKey) });
    }
  }
  
  /**
   * Setup session cleanup (SaMD best practice)
   * Clear cache on page unload to prevent PHI persistence
   */
  setupSessionCleanup() {
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.clearAll();
        this.logAudit('CACHE_SESSION_END', { reason: 'page_unload' });
      });
      
      // Also clear on visibility change (tab switch)
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          // Optional: Clear sensitive data when tab is hidden
          // this.clearPattern('residents:');
        }
      });
    }
  }
  
  /**
   * Audit logging for regulatory compliance
   * SaMD Requirement: Track all cache operations
   */
  logAudit(action, details = {}) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      action,
      ...details,
      sessionId: typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('session_id') : null
    };
    
    this.auditLog.push(logEntry);
    
    // Limit audit log size
    if (this.auditLog.length > this.maxAuditLogSize) {
      this.auditLog.shift();
    }
    
    // In production, send to backend for persistent audit trail
    if (process.env.NODE_ENV === 'production' && details.key) {
      this.sendAuditToBackend(logEntry);
    }
  }
  
  /**
   * Sanitize cache key for logging (remove PHI)
   */
  sanitizeKeyForLog(key) {
    // Only log the prefix, not the full key which may contain PHI
    const parts = key.split(':');
    return parts[0] + ':***';
  }
  
    /**
   * Send audit log to backend (async, non-blocking)
   * 
   * Sends cache audit logs to backend for compliance tracking.
   * Uses HTTP-only cookies for authentication (sent automatically).
   */
  async sendAuditToBackend(logEntry) {
    try {
      // Import API_BASE_URL dynamically to avoid circular dependencies
      const { API_BASE_URL } = await import('../config/environment.js');
      
      // Non-blocking audit log submission
      // Uses credentials: 'include' to send HTTP-only cookies
      fetch(`${API_BASE_URL}/security/audit/cache`, {
        method: 'POST',
        credentials: 'include', // Send HTTP-only cookies for authentication
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(logEntry),
        keepalive: true // Ensure it completes even if page unloads
      }).catch(() => {
        // Silently fail - don't block cache operations
        // Backend will log the error if endpoint is unavailable
      });
    } catch (error) {
      // Silently fail - audit logging should not break cache functionality
    }
  }
  
  /**
   * Get audit log (for compliance reporting)
   */
  getAuditLog() {
    return [...this.auditLog];
  }
  
  /**
   * Export cache statistics for compliance reporting
   */
  getComplianceReport() {
    return {
      stats: this.getStats(),
      auditLogSize: this.auditLog.length,
      cacheSize: this.cache.size,
      maxCacheSize: this.maxCacheSize,
      maxCacheSizeMB: this.maxCacheSizeMB,
      storageType: 'IN_MEMORY_ONLY',
      phiPersistence: 'NONE',
      sessionBased: true,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Get or fetch pattern - common caching pattern
   */
  async getOrFetch(key, fetchFn, ttl = null) {
    // Try to get from cache
    const cached = this.get(key);
    if (cached !== null) {
      return cached;
    }
    
    // Fetch fresh data
    const data = await fetchFn();
    
    // Store in cache
    this.set(key, data, ttl);
    
    return data;
  }
}

// Export singleton instance
const cacheService = new CacheService();
export default cacheService;
